import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Breadcrumbs,
  Link,
  Alert,
  FormControlLabel,
  Switch,
  Card,
  CardMedia,
  IconButton,
} from '@mui/material';
import { Delete as DeleteIcon, Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { brandsService } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ImageUpload from '../components/ImageUpload';

const API_BASE_URL = 'https://api.inklandtattoo.fr';

const BrandEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Déterminer si nous sommes en mode création
  const isNewBrand = location.pathname === '/brands/new';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: '',
    isActive: true,
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      if (!isNewBrand && id) {
        const brandData = await brandsService.getById(id);
        if (brandData && brandData.data) {
          setFormData({
            name: brandData.data.name || '',
            description: brandData.data.description || '',
            website: brandData.data.website || '',
            isActive: brandData.data.isActive !== undefined ? brandData.data.isActive : true,
          });

          // Charger le logo existant si disponible
          if (brandData.data.logo) {
            setLogoPreview(`${API_BASE_URL}${brandData.data.logo}`);
          }
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = async () => {
    if (!isNewBrand && id && logoPreview && !logoFile) {
      // Si c'est un logo existant, demander confirmation
      if (window.confirm('Voulez-vous supprimer le logo de cette marque ?')) {
        try {
          await brandsService.deleteLogo(id);
          setLogoPreview(null);
        } catch (error) {
          console.error('Erreur lors de la suppression du logo:', error);
          setError('Erreur lors de la suppression du logo');
        }
      }
    } else {
      // Si c'est juste une prévisualisation, la retirer
      setLogoFile(null);
      setLogoPreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');

      // Préparer les données avec FormData pour gérer le fichier
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('description', formData.description || '');
      submitData.append('website', formData.website || '');
      submitData.append('isActive', formData.isActive);
      
      // Ajouter le logo si un nouveau fichier a été sélectionné
      if (logoFile) {
        submitData.append('logo', logoFile);
      }

      if (isNewBrand) {
        await brandsService.create(submitData);
      } else {
        await brandsService.update(id, submitData);
      }

      navigate('/brands');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setError(error.response?.data?.message || 'Erreur lors de la sauvegarde de la marque');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  if (loading) {
    return <LoadingSpinner message="Chargement de la marque..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          href="/brands"
          onClick={(e) => {
            e.preventDefault();
            navigate('/brands');
          }}
          sx={{ cursor: 'pointer' }}
        >
          Marques
        </Link>
        <Typography color="text.primary">
          {isNewBrand ? 'Nouvelle marque' : 'Modifier la marque'}
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/brands')} color="primary">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" fontWeight="bold">
          {isNewBrand ? 'Nouvelle marque' : 'Modifier la marque'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Nom de la marque */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Nom de la marque"
                fullWidth
                required
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Ex: Nike, Adidas, Apple..."
              />
            </Grid>

            {/* Site web */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Site Web"
                fullWidth
                value={formData.website}
                onChange={(e) => handleChange('website', e.target.value)}
                placeholder="https://www.example.com"
                type="url"
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={4}
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Décrivez la marque..."
              />
            </Grid>

            {/* Logo */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Logo de la marque
              </Typography>
              
              {logoPreview && (
                <Card sx={{ maxWidth: 300, mb: 2, position: 'relative' }}>
                  <CardMedia
                    component="img"
                    image={logoPreview}
                    alt="Logo de la marque"
                    sx={{ height: 200, objectFit: 'contain', p: 2 }}
                  />
                  <IconButton
                    onClick={handleRemoveLogo}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'error.main', color: 'white' },
                    }}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Card>
              )}

              <Button
                variant="outlined"
                component="label"
              >
                {logoPreview ? 'Changer le logo' : 'Ajouter un logo'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleLogoChange}
                />
              </Button>
              <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                Formats acceptés : JPG, PNG, SVG, WEBP (max 5MB)
              </Typography>
            </Grid>

            {/* Statut actif */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                    color="primary"
                  />
                }
                label="Marque active"
              />
              <Typography variant="caption" display="block" color="text.secondary">
                Les marques inactives ne seront pas affichées dans les sélections
              </Typography>
            </Grid>

            {/* Boutons d'action */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/brands')}
                  disabled={saving}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving || !formData.name}
                  startIcon={<SaveIcon />}
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default BrandEdit;





















